import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import client from '../api/client';
import { getFriendlyApiError } from '../utils/apiErrors';
import { buildRentersInsuranceRequestPayload } from '../utils/rentersInsurancePayload';
import { isPetLeadFlow, isCarLeadFlow, trackFunnelStep, trackCarFunnelStep } from '../utils/analytics';
import { readLeadContactFromSession } from '../utils/leadSession';

function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const contact = readLeadContactFromSession();
    if (!contact) return;
    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName || contact.firstName || '',
      lastName: prev.lastName || contact.lastName || '',
      email: prev.email || contact.email || '',
      phone: prev.phone || contact.phone || '',
    }));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Register user (no automatic login until email is verified)
      await client.post('/auth/register', formData);

      // Check if there's insurance form data to submit
      const insuranceFormData = sessionStorage.getItem('insuranceFormData');
      if (insuranceFormData) {
        try {
          const formDataObj = JSON.parse(insuranceFormData);
          // Requires login; kept in sessionStorage until user verifies and signs in (LoginPage replays).
          await client.post('/insurance/request', buildRentersInsuranceRequestPayload(formDataObj));
          sessionStorage.removeItem('insuranceFormData');
          sessionStorage.removeItem('insuranceType');
          sessionStorage.removeItem('zipCode');
        } catch (insuranceError) {
          console.error('Failed to submit insurance request:', insuranceError);
          // Don't fail the whole signup, just log it
        }
      }

      // Persist email so /verify-email can resend without requiring login
      sessionStorage.setItem('pendingVerificationEmail', formData.email);

      if (isPetLeadFlow()) {
        trackFunnelStep('signup_complete');
      }
      if (isCarLeadFlow()) {
        trackCarFunnelStep('signup_complete');
      }

      // After registration, direct user to email verification instructions
      sessionStorage.removeItem('redirectAfterSignup');
      navigate('/verify-email');
    } catch (err: any) {
      const status = err?.response?.status;
      const message = getFriendlyApiError(err, 'Registration failed. Please try again.');
      if (status === 409) {
        // Account already exists → send user to login to proceed
        navigate('/login?existing=1');
        return;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ maxWidth: '400px', margin: '2rem auto' }}>
      <div className="card">
        <div className="card-header">Create Your Account</div>
        <form onSubmit={handleSubmit}>
          {error && <div className="alert alert-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              placeholder="John"
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              placeholder="Doe"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              id="phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              autoComplete="tel"
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="At least 8 characters"
            />
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', color: '#4B5563' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--color-green)', fontWeight: '600' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default RegisterPage;
